import { useApi } from "~/composables/useApi";
import { useSupabase } from "~/composables/useSupabase";
import type { FullDocument } from "@asap/shared/schemas/document.schemas";
import type { ComputedRef, Ref } from "vue";
import type { AnyDocumentTypeHandler } from "../handlers/handler";
import { findHandlerByDatabaseType } from "../handlers/registry";

export interface UseDocumentRecordOptions {
  documentId: Ref<string | undefined>;
  includeReplaced?: Ref<boolean> | boolean;
}

export interface DocumentActorInfo {
  name: string;
}

export interface UseDocumentRecordReturn {
  // Refs
  document: Ref<FullDocument | null>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;

  // Computed values
  adapter: ComputedRef<AnyDocumentTypeHandler | null>;

  // File fetching
  fetchFiles: () => Promise<File[]>;

  // Refetch
  refetch: () => Promise<void>;
}

export function useDocumentRecord(options: UseDocumentRecordOptions): UseDocumentRecordReturn {
  const { documentId, includeReplaced } = options;
  const includeReplacedValue = computed(() => unref(includeReplaced) ?? false);

  const document = ref<FullDocument | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const adapter = computed<AnyDocumentTypeHandler | null>(() => {
    if (!document.value?.type) return null;
    return findHandlerByDatabaseType(document.value.type);
  });

  async function fetchDocument() {
    if (!documentId.value) {
      document.value = null;
      error.value = null;
      isLoading.value = false;
      return;
    }

    const { invoke } = useApi();

    isLoading.value = true;
    error.value = null;
    document.value = null;

    try {
      const response = await invoke({
        path: "document/:id",
        method: "GET",
        params: { id: documentId.value },
        query: { includeReplaced: includeReplacedValue.value || undefined },
      });

      if (!response.success) {
        error.value = "Document introuvable";
        return;
      }

      document.value = response.data;
    } catch {
      error.value = "Document introuvable";
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchFiles(): Promise<File[]> {
    if (!document.value) return [];

    const supabase = useSupabase();
    const fetchedFiles: File[] = [];

    for (const file of document.value.files) {
      if (!file.supabase_bucket_id || !file.supabase_name) continue;

      const { data, error: downloadError } = await supabase.storage
        .from(file.supabase_bucket_id)
        .download(file.supabase_name);

      if (downloadError) throw downloadError;

      fetchedFiles.push(new File([data], file.name, { type: file.mimetype }));
    }

    return fetchedFiles;
  }

  // Watch documentId and fetch on change
  watch(() => documentId.value, fetchDocument, { immediate: true });

  return {
    document,
    adapter,
    isLoading,
    error,
    fetchFiles,
    refetch: fetchDocument,
  };
}
